<template>
  <div class="dashboard-board-container">
    <!-- Board Filters Component -->
    <BoardFilters />
    
    <!-- Board View Component -->
    <BoardView board-type="jobs" @job-selected="onJobSelected" @job-moved="onJobMoved" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import BoardFilters from './BoardFilters.vue';
import BoardView from './BoardView.vue';
import ApplicationService from '@/services/application';
import ProjectServices from '@/services/projects';

const app = new ApplicationService();
const projectService = new ProjectServices();

export default {
  name: 'DashboardBoard',
  components: {
    BoardFilters,
    BoardView
  },
  data() {
    return {
      // No longer need projectController since we're using global app service
    };
  },
  methods: {
    ...mapActions('board', [
      'initializeBoardData',
      'loadFilterOptions',
      'selectJob'
    ]),

    async initializeBoard() {
      // Initialize board data and filter options
      await Promise.all([
        this.initializeBoardData(),
        this.loadFilterOptions()
      ]);

      // After board is initialized, check if we need to open a specific job
      await this.handleRouteJobId();
    },

    async handleRouteJobId() {
      // Check if there's a job ID in the route parameters
      const jobId = this.$route.params.id;
      if (!jobId) {
        return;
      }

      // First, try to load project data immediately to populate the side panel
      // This ensures the side panel shows data even before the board loads
      await this.loadProjectDataAndSetSidePanel(jobId);

      // Wait a moment for the board data to be fully loaded
      await this.$nextTick();
      
      // Try to find and select the job in the board data
      const job = await this.findJobById(jobId);
      if (job) {
        // Select the job in the board store
        this.selectJob(job);
      }
      // Note: If the job is not found in the board, we already have the side panel loaded with project data
    },

    async loadProjectDataAndSetSidePanel(jobId) {
      try {
        // Load project details directly from API
        const projectData = await projectService.getProjectDetails(parseInt(jobId));
        
        if (projectData && projectData.length > 0) {
          const project = projectData[0];
          
          // Create a job object compatible with the board system
          const job = {
            intProjectID: project.intProjectID || parseInt(jobId),
            id: project.projectRefNumber || jobId,
            status: project.status || '',
            client: project.businessName || '',
            location: `${project.location || ''}, ${project.province || ''}`.replace(/^,\s*/, ''),
            technicianStatus: project.technicianStatus || '',
            dueDate: project.dueDate || '',
            priority: project.priority || '',
            ...project
          };

          // Set the side panel data directly in the store
          const sidePanel = this.$store.getters.getSidePanel;
          sidePanel.isOpen = true;
          sidePanel.isSidePanelFrame = false;
          sidePanel.name = "MainSidePanel";
          sidePanel.data = job;
          
          // Ensure the panel object exists (this might be set by the router)
          if (!sidePanel.panelObj || !sidePanel.panelObj.show) {
            // Create a mock panel object if it doesn't exist
            sidePanel.panelObj = {
              show: () => {},
              hide: () => {},
              promise: Promise.resolve()
            };
          }

          // Update the store
          this.$store.dispatch("setSidePanel", sidePanel);
          
          return job;
        }
      } catch (error) {
        // Show a toast notification about the job not being found
        this.$bvToast.toast(`Job ${jobId} could not be found or loaded.`, {
          title: 'Job Not Found',
          variant: 'warning',
          autoHideDelay: 3000
        });
      }
      return null;
    },

    async findJobById(jobId) {
      // Get the board data from the store
      const boardColumns = this.$store.getters['board/filteredBoardColumns'] || [];
      
      // Search through all columns for the job
      // Check both intProjectID and id (project reference number)
      for (const column of boardColumns) {
        if (column.jobs) {
          const job = column.jobs.find(j => 
            j.intProjectID === parseInt(jobId) || 
            j.intProjectID === jobId ||
            j.id === jobId ||
            j.id === parseInt(jobId) ||
            String(j.intProjectID) === String(jobId) ||
            String(j.id) === String(jobId)
          );
          if (job) {
            return job;
          }
        }
      }
      return null;
    },

    onJobSelected(job) {
      // Open the same side panel as the dashboard
      this.openJobSidePanel(job);
    },

    onJobMoved(data) {
      // Handle job move - could trigger API call to update status
      // Job moved successfully - handled by board component
      
      // Optionally, trigger dashboard stats refresh if needed
      this.$store.dispatch('Dashboard/reloadDashboardStatsData', false);
    },

    openJobSidePanel(job) {
      // Use the same logic as TableHeaderAndRows.vue showPanel method
      if (job && job.intProjectID) {
        // Determine the current route path and construct the new path
        let currentPath;
        let route;
        
        // Check if we're currently on a board route or dashboard
        if (this.$route.name === 'board' || this.$route.path.startsWith('/board')) {
          // For board context, use board route pattern
          currentPath = "/board/" + job.intProjectID;
          route = {name: "board-job-details", params: {id: job.intProjectID}};
        } else if (this.$route.name === 'dashboard' || this.$route.path === '/dashboard' || this.$route.path === '/') {
          // For dashboard context, use projects route pattern
          currentPath = "/projects/details/" + job.intProjectID;
          route = {name: "projects-details", params: {id: job.intProjectID}};
        } else {
          // Default to projects route
          currentPath = "/projects/details/" + job.intProjectID;
          route = {name: "projects-details", params: {id: job.intProjectID}};
        }

        // Update the router to reflect the selection
        if (currentPath !== this.$route.path) {
          this.$router.push({name: route.name, params: {id: `${job.intProjectID}`}});
        }

        // Use the global app.showSidePanel method exactly like the dashboard table does
        // This will ensure the exact same side panel functionality
        
        // Convert job data to the format expected by showSidePanel (same as dashboard table)
        const projectData = {
          intProjectID: job.intProjectID,
          id: job.intProjectID,
          projectRefNumber: job.id,
          status: job.status,
          location: this.parseLocation(job.location),
          province: this.parseProvince(job.location),
          postalCode: this.parsePostalCode(job.location),
          businessName: job.client,
          // Add other fields that might be expected
          technicianStatus: job.technicianStatus || '',
          dueDate: job.dueDate || '',
          priority: job.priority || '',
          // Copy any other properties from the job object
          ...job
        };

        // Call showSidePanel with the same parameters as the dashboard table
        app.showSidePanel(null, projectData, route);
      }
    },

    parseLocation(fullLocation) {
      // Extract just the street address from "123 Main St, Vancouver, BC"
      const parts = fullLocation.split(',');
      return parts.length > 0 ? parts[0].trim() : fullLocation;
    },

    parseProvince(fullLocation) {
      // Extract province from "123 Main St, Vancouver, BC"
      const parts = fullLocation.split(',');
      return parts.length >= 3 ? parts[2].trim() : '';
    },

    parsePostalCode(fullLocation) {
      // This would need to be implemented based on your data format
      // For now, return empty string as the mock data doesn't include postal codes
      return '';
    }
  },

  watch: {
    // Watch for route changes to handle navigation between different jobs
    '$route'(to, from) {
      // Only handle board route changes with different job IDs
      if (to.name === 'board-job-details' && from.name === 'board-job-details' && to.params.id !== from.params.id) {
        this.handleRouteJobId();
      }
      // Handle navigation to board with job ID from other routes
      else if (to.name === 'board-job-details' && to.params.id) {
        this.handleRouteJobId();
      }
    }
  },

  async mounted() {
    await this.initializeBoard();
  }
};
</script>

<style scoped>
/* Dashboard Board Container - Full Height Layout */
.dashboard-board-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Ensure boards work in tab context */
.tab-pane {
  height: 100%;
}

.tab-content {
  height: 100%;
}
</style>
